import React from 'react';
import { Button, Loading } from '@brighterion/br-ui';
import { useHistory } from 'react-router-dom';
import { UseMutationResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { handleOnClickEvent, handleKeyEvent } from 'Utils';
import RulesHeader from 'Components/RulesHeader';
import ButtonGroup from 'Components/ButtonGroup';
import { EventNames, EventTypes, trackEvent } from '@brighterion/insights';

import styles from './AddTenantPageHeader.css';

type AddTenantPageHeaderProps = {
    tenantGroupId: string;
    mutation: UseMutationResult<PostTenantsToGroupPayload, unknown, PostTenantsToGroupPayload, unknown>;
};

const AddTenantPageHeader = ({ tenantGroupId, mutation }: AddTenantPageHeaderProps) => {
    const { t } = useTranslation();
    const { dirty, submitForm } = useFormikContext<TenantCheckboxTableForm>();
    const history = useHistory();
    const handleCancel = () => {
        history.push(`/tenants/tenant-group/${tenantGroupId}`);
    };

    const handleSubmit = () => {
        submitForm();
        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier: 'Tenant Groups - Add Tenants to Group',
                text: 'Add Tenant to Group'
            }
        });
    };

    return (
        <RulesHeader>
            <RulesHeader.Left title='Add Tenants To Group' />
            <RulesHeader.Right>
                <ButtonGroup direction='row'>
                    <Button
                        dataTestId='add-to-group-cancel-btn'
                        variant='tertiary'
                        size='small'
                        onClick={handleOnClickEvent(handleCancel)}
                        onKeyDown={handleKeyEvent(handleCancel)}
                        className={styles.cancleBtn}
                        disabled={mutation.isLoading}
                    >
                        {t('Cancel')}
                    </Button>
                    <Button
                        dataTestId='add-to-group-buttn'
                        variant='primary'
                        size='small'
                        onClick={handleOnClickEvent(handleSubmit)}
                        onKeyDown={handleKeyEvent(handleSubmit)}
                        className={styles.addToGroupBtn}
                        disabled={!dirty || mutation.isLoading}
                    >
                        {mutation.isLoading ? <Loading size='sm' fillName='white' /> : t('Add To Group')}
                    </Button>
                </ButtonGroup>
            </RulesHeader.Right>
        </RulesHeader>
    );
};

export default AddTenantPageHeader;
